/**
 * ScheduleMatrix
 * ----------------
 * Houses the paginated grid of time slots vs. rooms, including per-cell
 * capacity chips, smasher dots, and customer details.
 * Adds inline action hooks so Admins can launch Add/Edit slot modals from each cell.
 */
import { Fragment } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import RoomSummaryCard from './RoomSummaryCard';

const resolveFillChipColor = (booked, capacity) => {
  if (!capacity) {
    return 'default';
  }
  if (!booked) {
    return 'info';
  }
  if (booked >= capacity) {
    return 'error';
  }
  return 'warning';
};

const normalizePositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const sumBookedSlotQuantities = (bookedSlots = []) => {
  if (!Array.isArray(bookedSlots) || !bookedSlots.length) {
    return null;
  }
  const total = bookedSlots.reduce((sum, slot) => {
    const quantity = Number(slot?.quantity);
    return sum + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);
  return total > 0 ? total : null;
};

const resolveBookingPartySize = (booking) => {
  if (!booking) {
    return 0;
  }
  const normalizedPartySize = normalizePositiveNumber(booking.partySize);
  const slotQuantityValue = normalizePositiveNumber(booking.slotQuantity);
  const bookedSlotsTotal = sumBookedSlotQuantities(booking.bookedSlots);

  if (bookedSlotsTotal !== null && slotQuantityValue !== null) {
    return Math.max(bookedSlotsTotal, slotQuantityValue);
  }
  if (bookedSlotsTotal !== null) {
    return bookedSlotsTotal;
  }
  if (normalizedPartySize !== null) {
    return normalizedPartySize;
  }
  if (slotQuantityValue !== null) {
    return slotQuantityValue;
  }
  return 0;
};

const deriveCellBookedCount = (cell) => {
  if (!cell) {
    return 0;
  }
  if (typeof cell.bookedCount === 'number' && cell.bookedCount >= 0) {
    return cell.bookedCount;
  }
  if (Array.isArray(cell.bookings) && cell.bookings.length) {
    return cell.bookings.reduce((sum, booking) => sum + resolveBookingPartySize(booking), 0);
  }
  return 0;
};

const resolveCellBackground = (cell, bookedCount = 0) => {
  if (!cell) {
    return 'transparent';
  }
  if (cell.isBlocked) {
    return 'rgba(255, 193, 7, 0.08)';
  }
  if (cell.totalCapacity && bookedCount >= cell.totalCapacity) {
    return 'rgba(244, 67, 54, 0.08)';
  }
  if (bookedCount > 0) {
    return 'rgba(63, 81, 181, 0.08)';
  }
  return 'rgba(255,255,255,0.03)';
};

const SmasherDots = ({ cell, roomId, bookedCount }) => {
  const resolvedBookedCount =
    typeof bookedCount === 'number' ? bookedCount : deriveCellBookedCount(cell);
  const bookingCountFallback = Array.isArray(cell?.bookings) ? cell.bookings.length : 0;
  const total = Math.max(cell?.totalCapacity || 0, resolvedBookedCount, bookingCountFallback);
  if (!total) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        No slots configured
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {Array.from({ length: total }).map((_, index) => {
        const isBooked = index < resolvedBookedCount;
        return (
          <Tooltip
            key={`${roomId}-${cell.timeSlot}-${index}`}
            title={isBooked ? 'Booked smasher' : 'Open slot'}
            placement="top"
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: isBooked ? 'primary.main' : 'transparent',
                border: `1px solid ${
                  isBooked ? 'rgba(93, 53, 192, 0.9)' : 'rgba(244,243,248,0.4)'
                }`,
                opacity: cell.isBlocked ? 0.4 : 1,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

const BookingCustomers = ({ bookings = [], onEditBooking }) => {
  if (!bookings.length) {
    return null;
  }
  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      {bookings.map((booking) => {
        const label = [
          booking.customerName || 'Guest',
          booking.slotLabels?.length ? booking.slotLabels.join(', ') : null,
        ]
          .filter(Boolean)
          .join(' — ');
        const statusSuffix =
          booking.status && booking.status !== 'confirmed' ? ` (${booking.status})` : '';
        const email = booking.customerEmail || 'Email not provided';
        const phone = booking.customerPhoneNumber || 'Phone not provided';
        const partySizeValue = resolveBookingPartySize(booking);
        const partySizeDisplay = partySizeValue > 0 ? partySizeValue : '—';

        return (
          <Stack
            key={booking.bookingId || `${booking.roomId}-${booking.timeSlot}-${label}`}
            spacing={0.5}
            sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 1 }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {label}
                {statusSuffix}
              </Typography>
              {onEditBooking && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onEditBooking(booking)}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  Edit
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Party size: {partySizeDisplay}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Email: {email}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Phone: {phone}
            </Typography>
            {booking.specialNote ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Note: {booking.specialNote}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
};

const ScheduleMatrixCell = ({ cell, room, slot, onAddSlot, onEditBooking }) => {
  const roomId = room?.id || 'room';
  const bookedCount = deriveCellBookedCount(cell);
  const totalCapacity = cell && typeof cell.totalCapacity === 'number' ? cell.totalCapacity : bookedCount;
  const chipColor = cell ? resolveFillChipColor(bookedCount, totalCapacity || 0) : 'default';
  const slotValue = cell?.timeSlot || slot || null;
  const cellBackground = resolveCellBackground(cell, bookedCount);

  const handleAddSlot = () => {
    if (onAddSlot) {
      onAddSlot({ room, cell, slot: slotValue });
    }
  };

  const handleEditBooking = (booking) => {
    if (onEditBooking) {
      onEditBooking({ room, cell, slot: slotValue, booking });
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minHeight: 130,
        backgroundColor: cellBackground,
        transition: 'background-color 0.2s ease',
      }}
    >
      {cell ? (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">
              {cell.sessionLabels?.length ? cell.sessionLabels.join(' • ') : 'Session'}
            </Typography>
            <Chip
              size="small"
              label={`${bookedCount}/${totalCapacity || 0}`}
              color={chipColor}
              variant={chipColor === 'default' ? 'outlined' : 'filled'}
            />
          </Stack>
          {cell.isBlocked ? (
            <Chip size="small" label="Blocked" color="warning" variant="outlined" sx={{ mt: 2 }} />
          ) : (
            <>
              <SmasherDots cell={cell} roomId={roomId} bookedCount={bookedCount} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {cell.remainingCapacity > 0
                  ? `${cell.remainingCapacity} open`
                  : totalCapacity
                  ? 'Fully booked'
                  : 'No slots configured'}
              </Typography>
              {cell.slots.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {cell.slots.length} smash lanes
                </Typography>
              )}
            </>
          )}
          <BookingCustomers bookings={cell.bookings} onEditBooking={handleEditBooking} />
          {onAddSlot && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleAddSlot}
              sx={{ mt: 1 }}
            >
              {cell.isBlocked ? 'Override & add booking' : 'Add booking'}
            </Button>
          )}
        </>
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.disabled">
            No schedule
          </Typography>
          {onAddSlot && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => onAddSlot({ room, cell: null, slot })}
            >
              Add booking
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
};

const ScheduleMatrix = ({
  filteredRooms,
  paginatedMatrixRows,
  matrixRowsLength,
  rowsPerPage,
  currentPage,
  totalPages,
  visibleSlotRangeStart,
  visibleSlotRangeEnd,
  combinedLoading,
  onPageChange,
  hasMatrixData,
  onAddSlot,
  onEditBooking,
}) => (
  <Box sx={{ width: '100%' }}>
    {combinedLoading && <LinearProgress sx={{ mb: 2 }} />}
    {matrixRowsLength > 0 && (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          Showing {visibleSlotRangeStart}-{visibleSlotRangeEnd} of {matrixRowsLength} time slots
        </Typography>
        {matrixRowsLength > rowsPerPage && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
            color="primary"
            size="small"
            shape="rounded"
          />
        )}
      </Stack>
    )}
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `190px repeat(${filteredRooms.length}, minmax(220px, 1fr))`,
          gap: 1.5,
          minWidth: filteredRooms.length ? filteredRooms.length * 220 + 190 : '100%',
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
          <Typography variant="subtitle2">Time Slot</Typography>
          <Typography variant="caption" color="text.secondary">
            (30-minute blocks)
          </Typography>
        </Paper>
        {filteredRooms.map((room) => (
          <RoomSummaryCard key={room.id} room={room} />
        ))}

        {paginatedMatrixRows.map((row) => (
          <Fragment key={row.slot}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2">{row.displayTime}</Typography>
              <Typography variant="caption" color="text.secondary">
                {row.slot}
              </Typography>
            </Paper>
            {row.cells.map((cell, index) => {
              const room = filteredRooms[index];
              return (
                <ScheduleMatrixCell
                  key={`${row.slot}-${room?.id || index}`}
                  cell={cell}
                  room={room}
                  slot={row.slot}
                  onAddSlot={onAddSlot}
                  onEditBooking={onEditBooking}
                />
              );
            })}
          </Fragment>
        ))}
      </Box>
    </Box>
    {!hasMatrixData && !combinedLoading && (
      <Alert severity="info" sx={{ mt: 2 }}>
        No sessions are scheduled for the selected filters. Import a schedule or adjust the date range.
      </Alert>
    )}
  </Box>
);

export default ScheduleMatrix;